import type { PromptObject } from "prompts/index.js"
import type { CLIOptions, Framework, ProjectConfig } from "../types/types.js"
import { validateProjectName } from "./validators.js"

const frameworkMeta: Record<Framework, Array<string>> = {
  astro: ["none"],
  react: ["next", "remix", "none"],
  svelte: ["sveltekit", "none"],
  vanilla: ["none"],
  vue: ["nuxt", "none"],
}

export function getPrompts(
  projectName: string | undefined,
  options: CLIOptions,
  presetConfig: Partial<ProjectConfig>,
): Array<PromptObject> {
  const prompts: Array<PromptObject> = []

  if (!projectName) {
    prompts.push({
      initial: "my-app",
      message: "What is your project name?",
      name: "projectName",
      type: "text",
      validate: validateProjectName,
    })
  }

  if (!options.framework && !presetConfig.framework) {
    prompts.push({
      choices: [
        {
          description: "A JavaScript library for building user interfaces",
          title: "React",
          value: "react",
        },
        {
          description: "The progressive JavaScript framework",
          title: "Vue",
          value: "vue",
        },
        {
          description: "Cybernetically enhanced web apps",
          title: "Svelte",
          value: "svelte",
        },
        {
          description: "Build faster websites with less client-side JS",
          title: "Astro",
          value: "astro",
        },
        {
          description: "No framework, just JavaScript",
          title: "Vanilla JS",
          value: "vanilla",
        },
      ],
      initial: 0,
      message: "Choose a frontend framework:",
      name: "framework",
      type: "select",
    })
  }

  if (!options.meta && !presetConfig.metaFramework) {
    prompts.push({
      choices: (_prev: any, values: any) => {
        const framework =
          options.framework || values.framework || presetConfig.framework
        const metaOptions = frameworkMeta[framework as Framework] || ["none"]

        return metaOptions.map((meta) => {
          const titles: Record<string, string> = {
            next: "Next.js - Full-stack React framework",
            none: "None - Just the base framework",
            nuxt: "Nuxt - Full-stack Vue framework",
            remix: "Remix - Full-stack web framework",
            sveltekit: "SvelteKit - Full-stack Svelte framework",
          }

          return {
            title: titles[meta] || meta,
            value: meta,
          }
        })
      },
      initial: 0,
      message: "Choose a meta framework:",
      name: "metaFramework",
      type: (_prev: any, values: any) => {
        const framework =
          options.framework || values.framework || presetConfig.framework
        const metaOptions = frameworkMeta[framework as Framework]
        return metaOptions && metaOptions.length > 1 ? "select" : null
      },
    })
  }

  // TypeScript
  if (
    options.typescript === undefined &&
    presetConfig.typescript === undefined
  ) {
    prompts.push({
      active: "yes",
      inactive: "no",
      initial: true,
      message: "Use TypeScript?",
      name: "typescript",
      type: "toggle",
    })
  }

  // Tailwind CSS
  if (options.tailwind === undefined && presetConfig.tailwind === undefined) {
    prompts.push({
      active: "yes",
      inactive: "no",
      initial: true,
      message: "Add Tailwind CSS v4?",
      name: "tailwind",
      type: "toggle",
    })
  }

  // Package Manager
  if (!options.pm && !presetConfig.packageManager) {
    prompts.push({
      choices: [
        {
          description: "The default Node.js package manager",
          title: "npm",
          value: "npm",
        },
        {
          description: "Fast, disk space efficient package manager",
          title: "pnpm",
          value: "pnpm",
        },
        {
          description: "Fast, reliable, and secure dependency management",
          title: "yarn",
          value: "yarn",
        },
        {
          description: "All-in-one JavaScript runtime & toolkit",
          title: "bun",
          value: "bun",
        },
      ],
      initial: 1,
      message: "Choose a package manager:",
      name: "packageManager",
      type: "select", // Default to pnpm
    })
  }

  // Code Quality
  if (!options.quality && !presetConfig.codeQuality) {
    prompts.push({
      choices: [
        {
          description: "Fast formatter and linter, replaces ESLint & Prettier",
          title: "Biome",
          value: "biome",
        },
        {
          description: "Traditional setup with separate linter and formatter",
          title: "ESLint + Prettier",
          value: "eslint-prettier",
        },
      ],
      initial: 0,
      message: "Choose code quality tooling:",
      name: "codeQuality",
      type: "select",
    })
  }

  // Git Hooks
  if (options.gitHooks === undefined && presetConfig.gitHooks === undefined) {
    prompts.push({
      active: "yes",
      inactive: "no",
      initial: false,
      message: "Add Lefthook for Git hooks?",
      name: "gitHooks",
      type: "toggle",
    })
  }

  return prompts
}
